import { Button, Modal, TextEditorContent } from "@/components/basic";
import { EditMapResourceModal } from "@/components/map/EditMapResourceModal";
import SchedulesGroups from "@/components/map/SchedulesGroups";
import { OptionsMenu } from "@/components/partial/Cards/OptionsMenu";
import { DeleteResourceModal } from "@/components/partial/Modals/DeleteResourceModal";
import { ResourceSettingsPanel } from "@/components/partial/ResourceSettingsPanel/ResourceSettingsPanel";
import RestrictionAlert from "@/components/partial/RestrictionAlert/RestrictionAlert";
import { SideDrawer } from "@/components/partial/SideDrawer/SideDrawer";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/hooks/useCustomParkingVerbiage";
import { useEditResourceRouter } from "@/hooks/useEditResourceRouter";
import { useOfficeRestrictions } from "@/hooks/useRestrictions";
import { classNames } from "@/lib/classNames";
import { isRBFeature } from "@/lib/featureFlags";
import { canScheduleOthers } from "@/lib/permissions";
import { getResourceTypeName, isAllowedToReserve } from "@/lib/utils";
import {
  AvailabilityErrorCode,
  AvailabilityErrorDetails,
  IResource
} from "@gettactic/api";
import { forceReloadChrome96Bug } from "@gettactic/helpers/src/map/svg/forceReloadChrome96Bug";
import { InteractiveZone } from "@gettactic/helpers/src/map/types";
import {
  useAreas,
  useIsDesignSystem2,
  useMapSchedulable,
  useSettings
} from "@gettactic/hooks";
import { useAuthenticated } from "@gettactic/hooks";
import { IResourceWithSchedules } from "@gettactic/hooks";
import { XIcon } from "@heroicons/react/outline";
import { Spoiler } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInMinutes, format } from "date-fns";

import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { Image } from "@v2/components/display/Image/Image";
import { ResourceLabel } from "@v2/components/labels/ResourceLabel/ResourceLabel";
import React, {
  CSSProperties,
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import { HiOutlineOfficeBuilding as Office } from "react-icons/hi";
import { UseDisclosureReturn } from "@gettactic/helpers";

type ResourceDetailsProps = {
  setOpenResourceModal: Dispatch<SetStateAction<boolean>>;
  setSelectedResource: Dispatch<SetStateAction<IResource | null>>;
  openResourceModal: boolean;
  startDate: Date;
  endDate: Date | null;
  formattedDate: string;
  res: IResourceWithSchedules | null;
  zones: InteractiveZone[];
  floor: IResource;
  openReservation: (res: IResource) => void;
  resourcePermission: boolean;
  resources: IResourceWithSchedules[];
  setEditGroupId: Dispatch<SetStateAction<string | null>>;
  officeId: string;
  editGroupId: string | null;
  svg: SVGSVGElement;
  setMapChanges: Dispatch<SetStateAction<number>>;
  setPopperElement: Dispatch<SetStateAction<HTMLElement | null>>;
  styles: { [p: string]: CSSProperties };
  attributes: { [p: string]: { [p: string]: string } | undefined };
  officeTimeZone: string;
  meetingsDisabled: boolean;
  embedded?: boolean;
  restrictionsFromSchedulable?: boolean;
  isReservationMode?: boolean;
  fullscreenMapDisclosure?: UseDisclosureReturn;
};

export default React.memo(ResourceDetails);

function ResourceDetails({
  res,
  floor,
  openReservation,
  openResourceModal,
  setOpenResourceModal, 
  startDate,
  endDate,
  formattedDate,
  resourcePermission,
  resources,
  editGroupId,
  officeId,
  setEditGroupId,
  svg,
  setMapChanges,
  setPopperElement,
  styles,
  attributes,
  officeTimeZone,
  meetingsDisabled,
  embedded,
  setSelectedResource,
  restrictionsFromSchedulable,
  isReservationMode = false,
  fullscreenMapDisclosure
}: ResourceDetailsProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isV2 = useIsDesignSystem2();
  const allowReserveOnBehalf = canScheduleOthers(authenticatedUser);
  const offices = authenticatedUser.offices;
  const {
    openResourceId,
    isSideDrawerOpen,
    setIsSideDrawerOpen,
    resourceContext,
    setDeleteModal,
    deleteModal
  } = useEditResourceRouter(officeId, res?.type ? [res?.type] : []);

  const calculatedDuration = endDate ? differenceInMinutes(endDate, startDate) : 0;
  const duration = calculatedDuration > 0 ? calculatedDuration : 0;
  const weekDatesStr = [format(startDate, "yyyy-MM-dd")]; // weekDates.map((weekDate) => format(weekDate, 'yyyy-MM-dd'));

  const restrictions = useOfficeRestrictions(
    res?.office_id ?? null,
    startDate,
    weekDatesStr,
    duration,
    res?.id ? [res?.id] : [],

    // we only want to fetch restrictions separately from the old map
    // also prevent request when duration is invalid (endDate < startDate)
    !restrictionsFromSchedulable && duration > 0
  ).restrictions;

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const response = useMapSchedulable({
    officeId: officeId,
    parentId: floor ? floor.id : null,
    startDate: startDate.toISOString(),
    endDate: endDate?.toISOString(),
    duration: null,
    hierarchy: false,
    coreApi: true,

    // it's available from the new map
    enabled: restrictionsFromSchedulable
  });

  let availabilityErrors:
    | {
        reasonCode: AvailabilityErrorCode;
        details?: AvailabilityErrorDetails;
      }[]
    | undefined;
  if (restrictionsFromSchedulable && response.nonAvailable.length > 0) {
    availabilityErrors = response.nonAvailable
      .filter((r) => r.resource.id === res?.id)
      .map((r) => {
        return {
          reasonCode: r.reason_code as AvailabilityErrorCode,
          details: r.details as AvailabilityErrorDetails | undefined
        };
      });
  }

  const queryClient = useQueryClient();
  const [editMapPointModal, setEditMapPointModal] = useState(false);
  const entity = res?.type ? res.type : "desk";
  const office =
    offices?.officeId && res?.office_id
      ? offices?.byId[res?.office_id]
      : undefined;
  const closeModal = useCallback(() => {
    setOpenResourceModal(false);
  }, [setOpenResourceModal]);
  const openEditMapPointModal = useCallback(() => {
    closeModal();
    setEditMapPointModal(true);
  }, [closeModal, setEditMapPointModal]);
  const openEditModal = useCallback(() => {
    closeModal();
    if (res?.id) {
      openResourceId(res?.id);
    }
  }, [closeModal, openResourceId, res]);
  useEffect(() => {
    const keyBinding = (e: KeyboardEvent) => {
      if (!resourcePermission || !openResourceModal) {
        return;
      }
      if (e.key === "e" && !isSideDrawerOpen) {
        openEditModal();
      } else if (editGroupId && e.key === "m" && !editMapPointModal) {
        openEditMapPointModal();
      }
    };
    document.addEventListener("keydown", keyBinding);
    return () => document.removeEventListener("keydown", keyBinding);
  }, [
    isSideDrawerOpen,
    editMapPointModal,
    editGroupId,
    openResourceModal,
    openEditMapPointModal,
    openEditModal,
    resourcePermission
  ]);
  const allowReserveByMaxDate = useMemo(() => {
    return isAllowedToReserve(
      startDate,
      officeTimeZone,
      office,
      entity,
      authenticatedUser.user?.organization_user?.roles
    );
  }, [startDate, officeTimeZone, office, entity]);

  const allowReserve = useMemo(() => {
    if (!res || !res.is_available) {
      return false;
    }
    // If the user can reserve on behalf of others, we need to be able to trigger the reserve modal
    // with the resource pre-selected.
    if (allowReserveOnBehalf) {
      return true;
    }

    return (
      allowReserveByMaxDate &&
      res.available_slots > 0 &&
      (res.type !== "meeting_room" || !meetingsDisabled)
    );
  }, [allowReserveByMaxDate, allowReserveOnBehalf, meetingsDisabled, res]);

  const ref = useOnclickOutside(closeModal);

  const customParkingVerbiage = useCustomParkingVerbiage();
  const editMenu = useMemo(() => {
    const menu: { text: string; onClick: () => void }[] = [];
    if (res) {
      menu.push({
        text: `Edit ${getResourceTypeName(res, customParkingVerbiage)}`,
        onClick: openEditModal
      });
    }
    if (editGroupId && svg) {
      menu.push({
        text: "Assign Node Resource",
        onClick: openEditMapPointModal
      });
    }

    return menu;
  }, [
    res,
    editGroupId,
    openEditMapPointModal,
    openEditModal,
    svg,
    customParkingVerbiage
  ]);

  const closeEditMapModal = useCallback(() => {
    setEditMapPointModal(false);
    setEditGroupId(null);
    setTimeout(forceReloadChrome96Bug, 500);
  }, [setEditMapPointModal, setEditGroupId]);

  const schedules = res?.schedules?.length ? res?.schedules : [];
  const rawEditResource = resources.find((x) => x.id === res?.id);

  const amenities = React.useMemo(() => {
    return res?.tags?.filter((tag) => tag.type === "amenity") || [];
  }, [res?.tags]);

  const { isOrgPrivacyEnabled } = useSettings();

  // Lookup for all possibly assignable resources
  // joining free resources (no parent_id) or resources
  // that belongs to a area that belongs to the Floor
  const { areas: childAreas } = useAreas(officeId, floor.id);
  const validParentIds = (childAreas?.elements ?? [])
    .map((x) => x.id)
    .concat([floor.id]);
  const assignableResources = resources.filter(
    (x) => validParentIds.includes(x.parent_id ?? "") || x.parent_id === null
  );
  const onEditResource = async (hasError: boolean) => {
    if (!hasError && rawEditResource) {
      queryClient.invalidateQueries([
        "organizations.resourcesAll",
        rawEditResource.office_id
      ]);
      queryClient.invalidateQueries([
        "organizations.schedulable",
        rawEditResource.office_id
      ]);
    }
  };
  const onDeleteClick = () => {
    setDeleteModal(
      resourceContext.state.selected ? [resourceContext.state.selected] : []
    );
  };
  const onCancel = () => {
    setIsSideDrawerOpen(false);
  };

  const handleRestrictionAlert = (): ReactElement | undefined => {
    const showRBRestriction =
      isRBFeature(authenticatedUser) && res?.type === "meeting_room";
    if (!isRBFeature(authenticatedUser)) {
      if (res && !allowReserve && (restrictions || availabilityErrors)) {
        return (
          <RestrictionAlert
            resourceId={res.id}
            restrictions={restrictions?.restrictions ?? []}
            availabilityErrors={availabilityErrors}
            className="mb-5 max-w-xl"
          />
        );
      }

      return undefined;
    }

    if (
      res &&
      !allowReserve &&
      (restrictions || availabilityErrors) &&
      showRBRestriction
    ) {
      return (
        <RestrictionAlert
          resourceId={res.id}
          restrictions={restrictions?.restrictions ?? []}
          availabilityErrors={availabilityErrors}
          className="mb-5 max-w-xl"
        />
      );
    }

    return undefined;
  };

  const handleReserveButton = (
    className?: string
  ): ReactElement | undefined => {
    if (!isRBFeature(authenticatedUser)) {
      if (res) {
        return (
          <div className="md:my-2">
            <Button
              disabled={!allowReserve}
              font="boldPrimary"
              onClick={(ev) => {
                if (!allowReserve) {
                  ev.preventDefault();
                  return;
                }
                fullscreenMapDisclosure?.close();
                setOpenResourceModal(false);
                openReservation(res);
              }}
              className={className}
            >
              Reserve
            </Button>
          </div>
        );
      }

      return undefined;
    }

    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!res?.is_available) {
      return (
        <div className="my-2">
          <Button
            disabled={!allowReserve}
            font="boldPrimary"
            onClick={(ev) => {
              if (!allowReserve) {
                ev.preventDefault();
                return;
              }
              setOpenResourceModal(false);
              openReservation(res);
            }}
          >
            Reserve
          </Button>
        </div>
      );
    }

    return undefined;
  };

  const resourceMediaUrl = res ? getResourceCoverUrl(res, 94) : undefined;

  return (
    <>
      <SideDrawer
        size={800}
        maxWidthStyle="max-w-[800px]"
        open={isSideDrawerOpen}
        setOpen={setIsSideDrawerOpen}
        title={`Edit ${resourceContext.state.selected?.name ?? ""}`}
        onClose={() => setIsSideDrawerOpen(false)}
      >
        <ResourceSettingsPanel
          onEdit={onEditResource}
          onDeleteClick={onDeleteClick}
          onCancel={onCancel}
        />
      </SideDrawer>

      <Modal
        title="Edit Map Resource"
        opened={editMapPointModal}
        onClose={closeEditMapModal}
        size="max-w-[38rem]"
      >
        <EditMapResourceModal
          setMapChanges={setMapChanges}
          onClose={closeEditMapModal}
          editGroupId={editGroupId}
          svg={svg}
          resources={assignableResources}
        />
      </Modal>
      <DeleteResourceModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />
      <div
        ref={setPopperElement}
        id={"resource-details-popper"}
        style={isMd ? styles.popper : undefined}
        {...(isMd ? attributes.popper : undefined)}
        className={classNames(
          "rounded-md border border-gray-400 bg-white shadow-none [.tactic-v2_&]:rounded-[8px] [.tactic-v2_&]:border-neutral-200 [.tactic-v2_&]:shadow-2xl",
          !openResourceModal && "hidden",
          "z-50",
          !isMd ? "fixed right-2.5 bottom-28 left-2.5 p-2" : undefined
        )}
      >
        <div
          ref={ref}
          className="mx-auto w-full max-w-lg rounded-md md:w-screen"
        >
          <div className="flex h-full flex-col overflow-y-auto rounded-md bg-white md:py-4 [.tactic-v2_&]:rounded-[8px]">
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 md:block">
              <button
                className="rounded-md bg-white text-gray-200 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                onClick={() => {
                  closeModal();
                  setSelectedResource(null);
                }}
              >
                <span className="sr-only">Close panel</span>
                <XIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="relative flex-1 px-0 md:mt-6 md:px-4">
              <div className="relative flex flex-col justify-items-center md:min-h-[140px]">
                <div className="flex gap-4">
                  {!isV2 && (
                    <div className="mr-4 inline-flex h-14 w-14 items-center justify-center rounded-full border border-transparent bg-gray-300 text-white shadow-sm">
                      {resourceMediaUrl ? (
                        <Image
                          src={resourceMediaUrl}
                          className={"object-cover"}
                        />
                      ) : (
                        <Office className="h-8 w-8" />
                      )}
                    </div>
                  )}
                  <div className="w-full font-normal text-base text-gray-800 md:ml-2">
                    <div className="flex w-full items-center justify-between">
                      {!isV2 && (
                        <div>
                          <h2 className="font-bold text-xl">
                            {res
                              ? res.name
                              : "Node is not assigned to any resource"}
                          </h2>
                          {res && floor ? <span>{floor.name}</span> : null}
                        </div>
                      )}
                      {isV2 && res && (
                        <div className={"w-full min-w-0 md:pb-4"}>
                          <ResourceLabel
                            className={"w-full min-w-0"}
                            headingClassName={"text-lg"}
                            resource={res}
                            key={res.id}
                            showAmenities={false}
                            allowFavorite={false}
                          />
                        </div>
                      )}

                      {isMd &&
                      resourcePermission &&
                      !!editMenu.length &&
                      !embedded ? (
                        <div className="ml-auto pt-1">
                          <OptionsMenu editMenuItems={editMenu} />
                        </div>
                      ) : null}
                    </div>
                    {res?.description ? (
                      <>
                        <div className="my-2 max-h-[200px] overflow-y-auto">
                          <Spoiler
                            maxHeight={175}
                            showLabel="Show more"
                            hideLabel="Hide"
                          >
                            <TextEditorContent value={res.description} />
                          </Spoiler>
                        </div>
                      </>
                    ) : null}
                    {amenities?.length > 0 && (
                      <div className={"flex flex-wrap gap-1 pt-1"}>
                        {amenities.map((amenity) => (
                          <span
                            key={amenity.id}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-100 px-2 py-1 font-medium text-gray-900 text-xs ring-1 ring-gray-200 ring-inset"
                          >
                            {amenity.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <div>
                      {res?.schedules && schedules?.length ? (
                        <div className="mt-2">
                          <h5 className="font-bold text-secondary text-sm [.tactic-v2_&]:text-secondary-foreground">
                            Schedules for {formattedDate}
                          </h5>
                          <SchedulesGroups
                            schedules={res.schedules}
                            fullVersion={true}
                            isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                          />
                        </div>
                      ) : null}
                      {isMd &&
                        res &&
                        res.available_slots > 0 &&
                        (res.reserved_slots ? (
                          <>
                            <span className="ml-2 rounded-full px-2.5 py-0.5 font-medium text-xs">
                              Reserved {res.reserved_slots}
                            </span>
                          </>
                        ) : null)}
                    </div>

                    {handleRestrictionAlert()}
                    {isMd && handleReserveButton()}
                    {!isMd && (
                      <div className={"py-2 text-right"}>
                        {handleReserveButton()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
